(() => {
  if (customElements.get('size-guide')) {
    return;
  }

  let tabCount = 0;

  class SizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.abortController) {
        return;
      }
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('.sg__trigger');
      if (!this.dialog) {
        return;
      }
      this.abortController = new AbortController();
      const {signal} = this.abortController;

      this.trigger?.addEventListener('click', () => this.open(this.trigger), {signal});
      // A click on the dialog element itself is a click on its backdrop.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('.sg__close')) {
          this.close();
        }
      }, {signal});
      // Escape: animate out instead of closing at once.
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, {signal});
      this.setupTabs();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      document.documentElement.classList.remove('sg-lock');
    }

    // Several tables (cm and inches, body and product measurements) become tabs.
    setupTabs() {
      const panels = [...this.querySelectorAll('.sg__table')];
      const tabs = this.querySelector('.sg__tabs');
      if (!tabs || panels.length < 2) {
        panels.forEach((panel) => panel.removeAttribute('role'));
        return;
      }
      const prefix = `sg-${++tabCount}`;
      const buttons = panels.map((panel, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sg__tab';
        button.id = `${prefix}-tab-${index}`;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', `${prefix}-panel-${index}`);
        button.textContent = panel.dataset.title || `Chart ${index + 1}`;
        panel.id = `${prefix}-panel-${index}`;
        panel.setAttribute('aria-labelledby', button.id);
        return button;
      });
      const select = (index, focus) => {
        buttons.forEach((button, i) => {
          button.setAttribute('aria-selected', String(i === index));
          button.tabIndex = i === index ? 0 : -1;
          panels[i].hidden = i !== index;
        });
        if (focus) {
          buttons[index].focus();
        }
      };
      tabs.addEventListener('click', (event) => {
        const index = buttons.indexOf(event.target.closest('.sg__tab'));
        if (index >= 0) {
          select(index, false);
        }
      });
      tabs.addEventListener('keydown', (event) => {
        const current = buttons.indexOf(document.activeElement);
        const step = {ArrowRight: 1, ArrowLeft: -1}[event.key];
        if (current >= 0 && step) {
          event.preventDefault();
          select((current + step + buttons.length) % buttons.length, true);
        }
      });
      tabs.replaceChildren(...buttons);
      tabs.hidden = false;
      this.classList.add('sg--tabbed');
      select(0, false);
    }

    open(opener) {
      if (!this.dialog) {
        return;
      }
      window.clearTimeout(this.closeTimer);
      if (this.dialog.open) {
        // Reopened while closing.
        this.dialog.classList.add('is-open');
        document.documentElement.classList.add('sg-lock');
        return;
      }
      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      document.documentElement.classList.add('sg-lock');
      this.dialog.querySelector('.sg__body').scrollTop = 0;
      requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));
    }

    close() {
      if (!this.dialog?.open) {
        return;
      }
      this.dialog.classList.remove('is-open');
      document.documentElement.classList.remove('sg-lock');
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.closeTimer = window.setTimeout(() => {
        this.dialog.close();
        this.opener?.focus({preventScroll: true});
      }, reduce ? 0 : 380);
    }
  }

  customElements.define('size-guide', SizeGuide);
})();
